import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "@mynaui/icons-react";
import assetService from "../../../services/assetService";

const spinInputSx = {
  width: 100,
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: "#E2E8F0" },
    paddingRight: "4px",
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 4px 8px 12px",
    fontSize: "0.875rem",
    MozAppearance: "textfield",
    "&::-webkit-outer-spin-button": { display: "none" },
    "&::-webkit-inner-spin-button": { display: "none" },
  },
};

const NumberSpinField = ({ field, value, min = 0, max, onFilterChange }) => (
  <TextField
    type="number"
    value={value}
    onChange={(e) => onFilterChange(field, e.target.value)}
    size="small"
    sx={spinInputSx}
    slotProps={{
      htmlInput: { min, max },
      input: {
        endAdornment: (
          <InputAdornment position="end">
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <IconButton
                size="small"
                disableRipple
                onClick={() =>
                  onFilterChange(
                    field,
                    max !== undefined
                      ? Math.min(max, Number(value || 0) + 1)
                      : Number(value || 0) + 1,
                  )
                }
                sx={{
                  p: "1px",
                  color: "#64748B",
                  "&:hover": { color: "#020618" },
                }}
              >
                <ChevronUp size={12} />
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                onClick={() =>
                  onFilterChange(field, Math.max(min, Number(value || 0) - 1))
                }
                sx={{
                  p: "1px",
                  color: "#64748B",
                  "&:hover": { color: "#020618" },
                }}
              >
                <ChevronDown size={12} />
              </IconButton>
            </Box>
          </InputAdornment>
        ),
      },
    }}
  />
);

const AssetFilters = ({ filters, onFilterChange }) => {
  const [criticalityOptions, setCriticalityOptions] = useState([
    "All Criticalities",
  ]);
  const [technicianOptions, setTechnicianOptions] = useState([
    "All Technicians",
  ]);

  useEffect(() => {
    assetService
      .getAssetPriorities()
      .then((res) => {
        const data = res?.data?.data ?? [];
        setCriticalityOptions([
          "All Criticalities",
          ...data.map((p) => p.label),
        ]);
      })
      .catch(() => {});

    assetService
      .getTechnicians()
      .then((res) => {
        const users = res?.data?.data?.users ?? [];
        setTechnicianOptions([
          "All Technicians",
          ...users.map((u) => u.username),
        ]);
      })
      .catch(() => {});
  }, []);

  const selectSx = {
    bgcolor: "#FFFFFF",
    borderRadius: "6px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
    "& .MuiSelect-select": {
      padding: "8px 12px",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "20px",
      color: "#020618",
    },
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        gap: 2.5,
        alignItems: "flex-end",
        flexWrap: "wrap",
        p: "24px",
        mt: "10px",
        bgcolor: "#F9FAFB",
        borderRadius: "12px",
        border: "1px solid #E2E8F0",
        overflow: "visible",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-9px",
          right: "48px",
          transform: "rotate(45deg)",
          width: "16px",
          height: "16px",
          backgroundColor: "#F9FAFB",
          borderLeft: "1px solid #E2E8F0",
          borderTop: "1px solid #E2E8F0",
          zIndex: 1,
        },
      }}
    >
      {/* Criticality */}
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 0.75,
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#020618",
          }}
        >
          Criticality
        </Typography>
        <Select
          value={filters.criticality}
          onChange={(e) => onFilterChange("criticality", e.target.value)}
          fullWidth
          size="small"
          IconComponent={(props) => <ChevronDown {...props} size={16} />}
          sx={selectSx}
          MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: "6px" } } }}
        >
          {criticalityOptions.map((opt) => (
            <MenuItem
              key={opt}
              value={opt}
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#020618",
              }}
            >
              {opt}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Technicians */}
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 0.75,
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#020618",
          }}
        >
          Technicians
        </Typography>
        <Select
          value={filters.technician}
          onChange={(e) => onFilterChange("technician", e.target.value)}
          fullWidth
          size="small"
          IconComponent={(props) => <ChevronDown {...props} size={16} />}
          sx={selectSx}
          MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: "6px" } } }}
        >
          {technicianOptions.map((opt) => (
            <MenuItem
              key={opt}
              value={opt}
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#020618",
              }}
            >
              {opt}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Health Score */}
      <Box>
        <Typography
          variant="body2"
          sx={{
            mb: 0.75,
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#020618",
          }}
        >
          Health Score &gt;=
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <NumberSpinField
            field="healthScoreMin"
            value={filters.healthScoreMin}
            min={0}
            max={100}
            onFilterChange={onFilterChange}
          />
          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>–</Typography>
          <NumberSpinField
            field="healthScoreMax"
            value={filters.healthScoreMax}
            min={0}
            max={100}
            onFilterChange={onFilterChange}
          />
        </Box>
      </Box>

      {/* RUL Range */}
      <Box>
        <Typography
          variant="body2"
          sx={{
            mb: 0.75,
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#020618",
          }}
        >
          RUL (days)
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <NumberSpinField
            field="rulMin"
            value={filters.rulMin}
            min={0}
            onFilterChange={onFilterChange}
          />
          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>–</Typography>
          <NumberSpinField
            field="rulMax"
            value={filters.rulMax}
            min={0}
            onFilterChange={onFilterChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AssetFilters;
